import React, {ReactElement} from 'react';
import {cx} from '@emotion/css';
import {GatsbyImage} from 'gatsby-plugin-image';

import DialogueCard from '@fo/designSystem/DialogueCard/DialogueCard';
import Button, {ButtonVariant} from '@fo/designSystem/Buttons/Button/Button';
import H, {HeaderType} from '@fo/designSystem/Text/Headers/Headers';
import P from '@fo/designSystem/Text/Paragraph/Paragraph';
import TrustpilotWidget from '@fo/designSystem/TrustpilotWidget/TrustpilotWidget';
import {StyleOverrides, GatsbyImageDataWithTitle} from '@fo/shared-types';

import getCombinedStyles from '@fo/shared-getters/getCombinedStyles';
import styles, {gatsbyImageElementStyle} from './CtaCard.styles';

export type ICtaCard = {
  buttonLabel: string;
  description: string;
  title: string;
  image: GatsbyImageDataWithTitle | {file: {url?: string}};
  onClose?(): void;
  slug: string;
  isShowCloseButton?: boolean;
  shouldShowTrustpilot?: boolean;
  classes?: StyleOverrides<typeof styles>;
};

function ArticleDialogWithCta({
  buttonLabel,
  description,
  title,
  image,
  onClose,
  slug,
  isShowCloseButton,
  shouldShowTrustpilot,
  classes,
}: ICtaCard): ReactElement {
  const classNames = getCombinedStyles(styles, classes);
  return (
    <div className={cx(classNames.container)}>
      <DialogueCard
        className={cx(classNames.dialogue)}
        innerClassName={cx(classNames.dialogueContents)}
        isHideCloseButton={!isShowCloseButton}
        onClose={onClose}
        name="CtaCard"
      >
        <div className={cx(classNames.contents)}>
          <div className={cx(classNames.imageContainer)}>
            {image &&
              ((image as GatsbyImageDataWithTitle).gatsbyImageData ? (
                <GatsbyImage
                  image={(image as GatsbyImageDataWithTitle).gatsbyImageData}
                  alt={(image as GatsbyImageDataWithTitle).title}
                  data-testid="ui.ctaCard.image.gatsbyImage"
                  imgClassName={cx(classNames.image)}
                  objectFit="contain"
                  style={gatsbyImageElementStyle}
                />
              ) : (
                <img
                  src={(image as {file: {url: string}}).file.url}
                  alt={(image as {title: string}).title}
                  className={cx(classNames.image)}
                  data-testid="ui.ctaCard.image.svg"
                />
              ))}
          </div>
          <div>
            {title && (
              <H className={cx(classNames.header)} headerType={HeaderType.H4} testId="ui.articleDialogWithCta.title">
                {title}
              </H>
            )}
            {description && (
              <P className={cx(classNames.paragraph)} testId="ui.articleDialogWithCta.description">
                {description}
              </P>
            )}
          </div>
        </div>
        <Button
          href={slug}
          ariaLabel={buttonLabel}
          variant={ButtonVariant.CALL_TO_ACTION}
          className={cx(classNames.button)}
        >
          {buttonLabel}
        </Button>
        {shouldShowTrustpilot && (
          <div className={cx(classNames.trustpilot)}>
            <TrustpilotWidget className={cx(classNames.trustpilotWidget)} />
          </div>
        )}
      </DialogueCard>
    </div>
  );
}

export default ArticleDialogWithCta;
